import ClearIcon from '@mui/icons-material/Clear';
import HideSourceIconBase from '@mui/icons-material/HideSource';
import { Autocomplete, Box, Chip, styled, TextField } from '@mui/material';
import { useMemo, useRef } from 'react';
import type { ColumnFilter, FilterOption } from 'util/filtering/models';
import { sentryMaskIf } from 'sentryReplayPrivacy';
import { StyledStack } from './TextCriteriaContent.styled';

/**
 * SEE-1834: filter keys whose options are people (or card names, which default to the
 * cardholder's): team members `name`/`email`/`username`, cards `cardholderName`/`name`, card and
 * project groups `groupAdmin`/`cardholder`, transactions `userId`. No other entity reuses these
 * keys for non-person values. Their chips, options and typed search are masked in replays.
 */
export const PERSON_VALUED_FILTER_KEYS: ReadonlySet<string> = new Set([
  'name',
  'email',
  'username',
  'cardholderName',
  'groupAdmin',
  'cardholder',
  'userId',
]);

const HideSourceIcon = styled(HideSourceIconBase)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
}));

const CustomChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  maxWidth: '100%',
  '& .MuiChip-deleteIcon': {
    width: theme.spacing(2.25),
    height: theme.spacing(2.25),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  '& .MuiChip-label': {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export const TextCriteriaContent = ({
  selectedFilterOptions,
  onSelectedFilterOptionsChange,
  filter,
}: {
  selectedFilterOptions: FilterOption[];
  onSelectedFilterOptionsChange: (newOptions: FilterOption[], key: string) => void;
  filter: ColumnFilter;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const replayMask = sentryMaskIf(PERSON_VALUED_FILTER_KEYS.has(filter.key));

  const filteredOptions = useMemo(() => {
    if (!filter.options) return [];
    // SEE-1154: match on id AND label so the project-scoped Sage cost-code variants
    // (same code id, different per-project name) stay independently selectable —
    // otherwise picking one drops its sibling from the list. Additive: every other
    // filter's options are unique by id anyway, so adding label is a no-op there.
    return filter.options.filter(
      (opt) => !selectedFilterOptions.some((c) => c.id === opt.id && c.label === opt.label)
    );
  }, [selectedFilterOptions, filter]);

  return (
    <>
      <StyledStack direction="row">
        {selectedFilterOptions.map((option, i) => {
          return (
            <CustomChip
              icon={<HideSourceIcon />}
              deleteIcon={<ClearIcon fontSize="small" color="info" />}
              key={i}
              color="primaryLighter"
              label={option.label}
              {...replayMask}
              onDelete={() => {
                const newValues = [...selectedFilterOptions];
                newValues.splice(
                  newValues.findIndex((val) => {
                    // SEE-1154: match label too so removing one per-project
                    // cost-code variant doesn't remove its same-id sibling.
                    return val.id === option.id && val.label === option.label;
                  }),
                  1
                );
                onSelectedFilterOptionsChange(newValues, filter.key);
              }}
            />
          );
        })}
      </StyledStack>
      <Autocomplete
        options={filteredOptions}
        value={null}
        onChange={(_, value, reason) => {
          if (reason === 'selectOption' && value) {
            onSelectedFilterOptionsChange([...selectedFilterOptions, value], filter.key);
            inputRef.current?.blur();
          }
        }}
        getOptionLabel={(option) => option.label}
        ListboxProps={replayMask}
        slotProps={{ popper: replayMask }}
        renderInput={(params) => (
          <Box className="flex items-center gap-8 border border-solid border-grey-300 rounded-lg px-8">
            <HideSourceIcon color="primary" />
            <TextField
              {...params}
              variant="standard"
              placeholder={filter.name}
              inputRef={inputRef}
              inputProps={{ ...params.inputProps, ...replayMask }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
            />
          </Box>
        )}
      />
    </>
  );
};
